import { h, Fragment } from 'preact';
import { useRef, useEffect } from 'preact/hooks';
import handleize from 'utils/handleize';
import { GLYPHS } from 'utils/constants';

const ProductMonogrammingCanvas = ({ customize, state }) => {
  let monogram = (customize?.monogram || []).find(
    (item) => item.style.label === state[state.activeTab]?.style,
  );

  if (state.activeTab === 'names') {
    monogram = (customize?.names || []).find(
      (item) => item.style.label === state[state.activeTab]?.font,
    );
  }
  const motif = (customize?.motif || []).find(
    (item) => item.style.label === state[state.activeTab]?.style,
  );

  const technicalDataEl = document.querySelector('[data-technical]');
  const { customizer } = JSON.parse(technicalDataEl.innerHTML);

  const { text, placement } = state[state.activeTab];
  const { scale = 100 } = placement;

  const renderText = (charSizes, copy, font) => {
    if (!copy || copy.length === 0) return null;

    /**
     * convertGlyphText converts a regular text string to the font's layout.
     * Using "a" as the base character, the offset for each character in the array is determined by a character offset array.
     *
     * Black magic. Don't Touch.
     *
     * @param {string} text - text value to convert to the gylph font.
     * @param {Array} characterOffsets - character code offsets for the glyph.
     * @returns {Array} - array of Strings representing the converted character set.
     */
    const convertGlyphText = (textToConvert, characterOffsets = null) => {
      const charArray = [...textToConvert.toLowerCase()];
      // The value of 'a' as a character code.
      const baseOffset = 'a'.charCodeAt(0); // 'a' = 97
      const charOffset = characterOffsets || [33, 65, baseOffset]; // the character offsets for the font

      return charArray.reduce((acc, char, index) => {
        const offset = charOffset[index] || baseOffset;
        // The character code magic to get the value based on offset.
        const charCode = char.charCodeAt(0) - baseOffset + offset;
        acc.push(String.fromCharCode(charCode));
        return acc;
      }, []);
    };

    // Calculate the offset based on the first text element
    const getDynamicHorizontalOverlapAdjustment = (firstTspan) => {
      if (!firstTspan.current) {
        return '0';
      }
      const firstTspanWidth = firstTspan.current.getComputedTextLength();

      let dx = '0';

      // Calculate the horizontal overlap adjustment
      dx = `${(firstTspanWidth * -0.8).toFixed(2)}px`;
      return dx;
    };

    // Calculate the offset based on the first text element
    const getDynamicVerticalOverlapAdjustment = (firstTspan) => {
      if (!firstTspan.current) {
        return '0';
      }
      const currentBBox = firstTspan.current.getBBox();
      let dy = '0';

      // Calculate the vertical overlap adjustment
      dy = `${(currentBBox.height * 0.125).toFixed(2)}px`;
      return dy;
    };

    const firstTspan = useRef(null);
    // eslint-disable-next-line
    const textToRender = Object.prototype.hasOwnProperty.call(
      GLYPHS,
      handleize(font),
    )
      ? convertGlyphText(copy, GLYPHS[handleize(font)].offset)
      : copy.split('');

    useEffect(() => {
      if (font === 'Crosby') {
        setTimeout(() => {
          const dx = getDynamicHorizontalOverlapAdjustment(firstTspan);
          const dy = getDynamicVerticalOverlapAdjustment(firstTspan);

          const secondTspan = firstTspan.current.nextSibling;
          if (!secondTspan || !secondTspan?.setAttribute) {
            return;
          }

          secondTspan.setAttribute('dx', dx);
          secondTspan.setAttribute('dy', dy);
        }, 100);
      } else {
        const secondTspan = firstTspan.current.nextSibling;
        if (!secondTspan || !secondTspan?.setAttribute) {
          return;
        }
        secondTspan.setAttribute('dx', '0');
        secondTspan.setAttribute('dy', '0');
      }
    }, [font, copy]);

    const mappedText = textToRender.map((char, index) => (
      // eslint-disable-next-line react/jsx-key
      <tspan
        fontSize={`${
          (charSizes || []).length > 1 ? charSizes[index] : charSizes[0]
        }em`}
        textAnchor="middle"
        dx="0"
        dy="0"
        ref={index === 0 ? firstTspan : null}
      >
        {char}
      </tspan>
    ));
    return mappedText;
  };

  return (
    <Fragment>
      {monogram && (
        <div
          className={`
            absolute w-full h-full top-0 left-0
            monogram-style__${monogram.style.label}
          `}
          style={{
            fontFamily: monogram.style.label,
          }}
        >
          {monogram.style.label === 'Potomac' ? (
            <div
              className="object-container"
              style={{
                top: `${monogram.style.vertical}%`,
                // eslint-disable-next-line max-len
                transform: `translate(-${placement.horizontal}%, -${placement.vertical}%)`,
                left: `${placement.horizontal}%`,
                fontSize: '1px',
                color: `${state[state.activeTab].colorHex}`,
              }}
            >
              <div style={{ fontSize: `${monogram?.text?.charSizes[0]}em` }}>
                •
              </div>
              <div
                className="mirror-text"
                style={{ fontSize: `${monogram?.text?.charSizes[1]}em` }}
              >
                {text[0]}
              </div>
              <div style={{ fontSize: `${monogram?.text?.charSizes[0]}em` }}>
                •
              </div>
              <div style={{ fontSize: `${monogram?.text?.charSizes[1]}em` }}>
                {text[1]}
              </div>
              <div style={{ fontSize: `${monogram?.text?.charSizes[0]}em` }}>
                •
              </div>
            </div>
          ) : monogram.style.label === 'Metallic Shadow' ? (
            <svg
              viewBox="0 0 460 460"
              className="product-canvas__text"
              fill={state[state.activeTab].colorHex}
            >
              <defs>
                <mask id="text-mask" x="0" y="0" width="100%" height="100%">
                  <text
                    x={`${placement.horizontal + 0.25}%`}
                    y={`${placement.vertical + 0.33}%`}
                    fontSize="1px"
                    fill="#fff"
                  >
                    {renderText(
                      monogram?.text?.charSizes,
                      text,
                      monogram.style.label,
                    )}
                  </text>
                </mask>
              </defs>
              {customizer.customizerMetallicShadowThreadImage && (
                <image
                  width="100%"
                  height="100%"
                  xlinkHref={customizer.customizerMetallicShadowThreadImage}
                  mask="url(#text-mask)"
                />
              )}
              <text
                x={`${placement.horizontal}%`}
                y={`${placement.vertical}%`}
                fontSize="1px"
              >
                {renderText(
                  monogram?.text?.charSizes,
                  text,
                  monogram.style.label,
                )}
              </text>
            </svg>
          ) : (
            <svg
              viewBox="0 0 460 460"
              className="product-canvas__text"
              fill={state[state.activeTab].colorHex}
            >
              <text
                x={`${placement.horizontal}%`}
                y={`${placement.vertical}%`}
                fontSize="1px"
              >
                {renderText(
                  monogram?.text?.charSizes,
                  text,
                  monogram.style.label,
                )}
                {monogram.style.label === 'Monroe' ? (
                  <tspan fontSize={`65em`} textAnchor="middle" dx="0" dy="0">
                    .
                  </tspan>
                ) : (
                  ''
                )}
              </text>
            </svg>
          )}
        </div>
      )}
      {motif && (
        <div
          className="absolute w-full h-full top-0 left-0"
          style={{
            fontFamily: state[state.activeTab]?.optionalFont,
          }}
        >
          {state[state.activeTab]?.motifImage && (
            <div
              className="absolute w-full h-full"
              style={{
                top: `${placement.vertical}%`,
                left: `${placement.horizontal}%`,
                transform: `translate(-50%, -50%) scale(${scale / 100})`,
                backgroundImage: `url(${state[state.activeTab]?.motifImage})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            >
              {state[state.activeTab]?.optionalFont !== '' && text.length && (
                <svg
                  viewBox="0 0 1380 460"
                  className="product-canvas__text min-w-[300%] left-1/2 max-w-none absolute bottom-[-25px]"
                  style={{
                    transform: `translate(-50%, 50%)`,
                  }}
                  fill={state[state.activeTab].colorHex}
                >
                  <text x="50%" y="67%" fontSize="1px">
                    {text.split('').map((char, index) => (
                      <tspan
                        key={index}
                        fontSize={`${
                          (motif.text.charSizes[0] / scale) * 100
                        }em`}
                        textAnchor="middle"
                      >
                        {char}
                      </tspan>
                    ))}
                  </text>
                </svg>
              )}
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default ProductMonogrammingCanvas;
